import { useTranslation } from '../../i18n';

interface QtyInputProps {
    /** Admin "Menge": `1` free number entry, `2` range picker. */
    mode: 1 | 2;
    min: number | undefined;
    max: number | undefined;
    value: number | undefined;
    onChange: (qty: number) => void;
    /** Accessible name, e.g. the Characteristic or Value label. */
    label: string;
}

/** Largest range we render as a picker when the admin left "Max." empty. */
const OPEN_RANGE = 100;

/**
 * Quantity field in front of a pick ("2 x Eiche"), as legacy renders it
 * (helper.js qtyInputHtml / qtySelectHtml): a number input for free entry,
 * a select from min to max for a range.
 */
export function QtyInput({ mode, min, max, value, onChange, label }: QtyInputProps) {
    const { t } = useTranslation();
    const lower = Math.max(0, min ?? 1);
    const upper = max !== undefined && max >= lower ? max : undefined;
    const current = clamp(value ?? Math.max(1, lower), lower, upper);
    const aria = `${t('characteristic.quantity')}: ${label}`;

    if (mode === 2) {
        const last = upper ?? lower + OPEN_RANGE - 1;
        const options: number[] = [];
        for (let q = lower; q <= last; q++) options.push(q);
        return (
            <span className="cfg-qty">
                <select
                    className="cfg-qty__input"
                    aria-label={aria}
                    value={current}
                    onChange={(e) => onChange(Number(e.target.value))}
                >
                    {options.map((q) => (
                        <option key={q} value={q}>{q}</option>
                    ))}
                </select>
                <span className="cfg-qty__times" aria-hidden="true">×</span>
            </span>
        );
    }

    return (
        <span className="cfg-qty">
            <input
                className="cfg-qty__input"
                type="number"
                inputMode="numeric"
                aria-label={aria}
                min={lower}
                max={upper}
                step={1}
                value={current}
                onChange={(e) => {
                    const n = Math.floor(Number(e.target.value));
                    if (Number.isFinite(n)) onChange(clamp(n, lower, upper));
                }}
            />
            <span className="cfg-qty__times" aria-hidden="true">×</span>
        </span>
    );
}

function clamp(n: number, lower: number, upper: number | undefined): number {
    if (n < lower) return lower;
    if (upper !== undefined && n > upper) return upper;
    return n;
}
